import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Store, Search as SearchIcon, BadgeCheck, Users, Loader2, MapPin, TrendingUp, Building2, Rocket, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import CompanyLogo from "@/components/CompanyLogo";

const TABS = [
  { id: "all", label: "All" },
  { id: "global", label: "Global 1000" },
];

export default function Directory() {
  const { user } = useApp();
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [industry, setIndustry] = useState("");
  const [country, setCountry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [globalTotal, setGlobalTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 30;

  const globalShown = items.filter((x) => x.source === "global").length;
  const reqIdRef = useRef(0);

  const load = useCallback(async (opts = {}) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      // Pagination applies to the Global 1000 slice; members always load on first page.
      const base = { q, industry, country };
      const params = opts.append
        ? { ...base, source: "global", skip: globalShown, limit: LIMIT }
        : { ...base, source, skip: 0, limit: LIMIT };
      const { data } = await api.get("/businesses", { params });
      if (myReq !== reqIdRef.current) return; // stale response, ignore
      setItems((prev) => (opts.append ? [...prev, ...(data.businesses || [])] : data.businesses || []));
      if (!opts.append) {
        setTotal(data.total || 0); setGlobalTotal(data.global_total || 0);
        if (data.industries?.length) setIndustries(data.industries);
        if (data.countries?.length) setCountries(data.countries);
      }
    } catch (e) { /* silent */ }
    finally { if (myReq === reqIdRef.current) setLoading(false); }
  }, [q, source, industry, country, globalShown]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source, industry, country]);

  const follow = async (b, e) => {
    e?.preventDefault();
    if (!user) { toast.error("Sign in to follow."); return; }
    try {
      const { data } = await api.post(`/businesses/${b.id}/follow`);
      setItems((arr) => arr.map((x) => x.id === b.id ? { ...x, is_following: data.following, follower_count: data.follower_count } : x));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const canLoadMore = source !== "member" && globalShown < globalTotal;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-28 pb-24">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Store size={14} /> Business Directory</span>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter mt-3">Discover businesses & organizations</h1>
        <p className="text-sec mt-2 text-sm">Browse and follow companies, governments and NGOs on My Kind — including the world's <b>Top 1000 companies by revenue</b>. Global profiles are AI-compiled from public info.</p>
      </motion.div>

      <Link to="/ventures" data-testid="directory-ventures-cta" className="mt-5 flex items-center gap-3 rud-card rounded-2xl p-4 hover:border-brand/50 transition-colors">
        <div className="w-10 h-10 rounded-xl bg-brand/15 grid place-items-center text-brand shrink-0"><Rocket size={18} /></div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm">Crowdfund · Raise & Fund · App Market</div>
          <div className="text-xs text-sec">Host your startup to seek funding, sell your mobile web app, or invest in ideas that match your thesis.</div>
        </div>
        <ArrowRight size={16} className="text-brand shrink-0" />
      </Link>

      <div className="relative mt-6 max-w-xl">
        <SearchIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sec" />
        <input data-testid="directory-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search by name, industry or tagline…" className="rud-input w-full rounded-full pl-10 pr-24 py-3 text-sm" />
        <button data-testid="directory-search-btn" onClick={() => load()} className="btn-primary absolute right-1.5 top-1/2 -translate-y-1/2 h-9 px-4 rounded-full text-sm">Search</button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        <div className="flex items-center gap-1 rounded-full border border-rud p-0.5 text-sm">
          {TABS.map((tb) => (
            <button key={tb.id} data-testid={`directory-tab-${tb.id}`} onClick={() => setSource(tb.id)}
              className={`px-4 py-1.5 rounded-full transition-colors ${source === tb.id ? "bg-brand text-black font-semibold" : "text-sec"}`}>{tb.label}</button>
          ))}
        </div>
        <select data-testid="directory-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} className="rud-input rounded-lg px-3 py-2 text-sm">
          <option value="">All industries</option>
          {industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select data-testid="directory-country" value={country} onChange={(e) => setCountry(e.target.value)} className="rud-input rounded-lg px-3 py-2 text-sm">
          <option value="">All countries</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {(industry || country) && <button data-testid="directory-clear-filters" onClick={() => { setIndustry(""); setCountry(""); }} className="text-xs text-sec hover:text-brand">Clear filters</button>}
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center gap-2 text-sec mt-10"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <p className="text-sec mt-10" data-testid="directory-empty">No businesses found yet.</p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="directory-grid">
            {items.map((b) => (
              <div key={b.id} className="rud-card rounded-2xl overflow-hidden" data-testid={`biz-${b.id}`}>
                <div className={`h-20 relative ${b.source === "global" ? "bg-gradient-to-br from-[var(--rud-brand)]/25 via-elevated to-elevated" : "bg-elevated"}`}>
                  {b.banner && <img src={b.banner} alt="" className="w-full h-full object-cover" />}
                  {b.source === "global" && !b.banner && <span className="absolute right-3 bottom-1 font-serif text-3xl text-[var(--rud-brand)]/25 select-none">{(b.name || "?").charAt(0)}</span>}
                  {b.source === "global" && b.rank != null && <span className="absolute top-2 left-2 text-[10px] font-mono text-brand bg-black/40 rounded-full px-2 py-0.5">#{b.rank} global</span>}
                </div>
                <div className="p-5 -mt-8">
                  <div className="w-14 h-14 rounded-xl bg-elevated border border-rud grid place-items-center overflow-hidden">
                    <CompanyLogo logo={b.logo} website={b.website} name={b.name} imgClassName="w-full h-full object-contain p-1.5 bg-white"
                      fallback={b.source === "global" ? <Building2 size={20} className="text-sec" /> : <Store size={20} className="text-sec" />} />
                  </div>
                  <div className="flex items-center gap-1.5 mt-3">
                    <Link to={`/businesses/${b.id}`} className="font-medium truncate hover:text-brand transition-colors" data-testid={`biz-open-${b.id}`}>{b.name}</Link>
                    {b.verified && <BadgeCheck size={16} className="text-brand shrink-0" data-testid={`biz-verified-${b.id}`} />}
                  </div>
                  <div className="text-xs text-brand">{b.industry || b.org_type}</div>
                  {b.hq && <div className="text-[11px] text-sec flex items-center gap-1 mt-0.5"><MapPin size={11} /> {b.hq}</div>}
                  {b.tagline && <p className="text-xs text-sec mt-1.5 line-clamp-2">{b.tagline}</p>}
                  {b.source === "global" && b.revenue && <div className="text-[11px] text-sec flex items-center gap-1 mt-1.5"><TrendingUp size={11} className="text-brand" /> {b.revenue}</div>}
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-[11px] text-sec flex items-center gap-1"><Users size={12} /> {b.follower_count}</span>
                    <button data-testid={`biz-follow-${b.id}`} onClick={(e) => follow(b, e)}
                      className={`rounded-full h-8 px-4 text-xs ${b.is_following ? "btn-ghost" : "btn-primary"}`}>
                      {b.is_following ? "Following" : "Follow"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {canLoadMore && (
            <div className="flex justify-center mt-8">
              <button data-testid="directory-load-more" onClick={() => load({ append: true })} disabled={loading} className="btn-ghost rounded-full h-11 px-6 flex items-center gap-2 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null} Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
